import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve the active tenant for the current request.
 *
 * Priority (matches backend `/api/tenants/resolve`):
 *   1. `?tenant=<slug>` query param  (used in preview/dev where subdomains
 *      aren't wired; also lets a Fleni-branded smart link work from any host).
 *   2. Subdomain of `window.location.hostname` (e.g. `fleni.thaleshealth.com`).
 *
 * Returns:
 *   - `tenant`: object with { id, name, slug, branding, legal_disclaimer_text }
 *     when resolved, else `null` (root / unknown subdomain -> B2B landing).
 *   - `loading`: true until the first resolution finishes.
 *
 * Notes:
 *   - We persist the resolved tenant slug in sessionStorage so navigating
 *     between pages doesn't cause a re-fetch flicker.
 *   - Failures fall back to `null` (B2B landing) rather than error UI: the
 *     platform must never block a visitor because tenant resolution failed.
 */
export function useTenant() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const explicit = (params.get("tenant") || "").trim().toLowerCase();
        const host = window.location.host;

        // Try session cache first (only when the explicit param matches or is
        // absent) so intra-app navigation doesn't blink.
        const cached = sessionStorage.getItem("resolved_tenant");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (
              parsed &&
              parsed.host === host &&
              (parsed.explicit || "") === explicit
            ) {
              if (!cancelled) {
                setTenant(parsed.tenant);
                setLoading(false);
              }
              return;
            }
          } catch (_) {
            /* ignore malformed cache */
          }
        }

        const qs = new URLSearchParams();
        qs.set("host", host);
        if (explicit) qs.set("tenant", explicit);

        const res = await fetch(`${API_URL}/api/tenants/resolve?${qs.toString()}`);
        if (!res.ok) throw new Error("resolve failed");
        const data = await res.json();
        const resolvedTenant = data?.tenant || null;

        try {
          sessionStorage.setItem(
            "resolved_tenant",
            JSON.stringify({ host, explicit, tenant: resolvedTenant })
          );
        } catch (_) {
          /* storage disabled -> just skip cache */
        }

        if (!cancelled) {
          setTenant(resolvedTenant);
          setLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setTenant(null);
          setLoading(false);
        }
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  return { tenant, loading };
}

/**
 * Derive display-ready branding from a tenant object, with sane fallbacks so
 * pages never crash when tenant resolution is still loading or missing.
 */
export function tenantBranding(tenant) {
  const b = tenant?.branding || {};
  return {
    name: tenant?.name || "Thales Health",
    slug: tenant?.slug || "",
    logoUrl: b.logo_url || null,
    primaryColor: b.primary_color || "#1d366b",
    accentColor: b.accent_color || "#E21856",
  };
}
